<template>
  <div class="admin-shell">
    <div class="admin-ambient admin-ambient-one" aria-hidden="true"></div>
    <div class="admin-ambient admin-ambient-two" aria-hidden="true"></div>

    <header class="admin-topbar">
      <div class="admin-brand">
        <span class="admin-brand-mark">WCF</span>
        <div>
          <strong>HYROX CONTROL</strong>
          <small>EVENT OPERATIONS</small>
        </div>
      </div>
      <div v-if="event" class="live-status" :class="{ online: event.registrationOpen }">
        <span></span>
        {{ event.registrationOpen ? "REGISTRATION LIVE" : "REGISTRATION OFFLINE" }}
      </div>
      <div class="topbar-actions">
        <button type="button" class="manual-action" @click="openManualRegistration">
          <v-icon size="18">mdi-account-plus-outline</v-icon>
          <span>Gratis anmelden</span>
        </button>
        <button type="button" class="preview-action" @click="openRegistrationPreview">
          <v-icon size="18">mdi-eye-outline</v-icon>
          <span>Zur Anmeldung</span>
        </button>
        <button type="button" class="icon-action" aria-label="Aktualisieren" @click="fetchData">
          <v-icon size="19" :class="{ spinning: loading }">mdi-refresh</v-icon>
        </button>
        <button type="button" class="export-action" @click="exportCsv">
          <v-icon size="18">mdi-tray-arrow-down</v-icon>
          <span>CSV Export</span>
        </button>
      </div>
    </header>

    <main class="admin-container">
      <section v-if="loading && !event" class="admin-loading">
        <div class="admin-loader">
          <v-progress-circular indeterminate color="#f4d35e" size="54" width="3" />
        </div>
        <strong>Control Center wird geladen</strong>
        <span>Anmeldungen und Kapazitäten werden synchronisiert…</span>
      </section>

      <template v-else-if="event">
        <section class="admin-hero">
          <div>
            <div class="hero-kicker">
              <span>{{ eventDateLabel }}</span>
              <i></i>
              {{ event.locationName || "Event" }}
            </div>
            <h1>{{ event.name }}</h1>
            <p>Live-Übersicht für Anmeldungen, Zahlungen, Kontingente und Unisex-Shirts der Teilnehmer.</p>
          </div>
          <div class="hero-health">
            <div class="health-ring" :style="{ '--usage': `${capacityUsage}%` }">
              <strong>{{ Math.round(capacityUsage) }}%</strong>
              <small>BELEGT</small>
            </div>
            <div class="health-copy">
              <small>GESAMTKAPAZITÄT</small>
              <strong>{{ capacityOccupied }} / {{ capacityTotal }}</strong>
              <span>{{ capacityRemaining }} Plätze frei</span>
            </div>
          </div>
        </section>

        <section class="metric-grid" aria-label="Event-Kennzahlen">
          <article class="metric-card metric-yellow">
            <div class="metric-icon">
              <v-icon size="22">mdi-account-multiple-outline</v-icon>
            </div>
            <div>
              <small>TEILNEHMER</small>
              <strong>{{ stats.activeParticipants }}</strong>
              <span>aktiv eingeplant</span>
            </div>
          </article>
          <article class="metric-card metric-blue">
            <div class="metric-icon">
              <v-icon size="22">mdi-ticket-confirmation-outline</v-icon>
            </div>
            <div>
              <small>ANMELDUNGEN</small>
              <strong>{{ stats.activeRegistrations }}</strong>
              <span>{{ pendingCount }} Zahlung(en) offen</span>
            </div>
          </article>
          <article class="metric-card metric-green">
            <div class="metric-icon">
              <v-icon size="22">mdi-cash-multiple</v-icon>
            </div>
            <div>
              <small>EINNAHMEN</small>
              <strong class="currency-value"> CHF {{ Number(stats.totalPaidRevenue || 0).toFixed(2) }} </strong>
              <span>bestätigt bezahlt</span>
            </div>
          </article>
          <article class="metric-card metric-coral">
            <div class="metric-icon">
              <v-icon size="22">mdi-alert-decagram-outline</v-icon>
            </div>
            <div>
              <small>MANUELL PRÜFEN</small>
              <strong>{{ reviewCount }}</strong>
              <span>{{ reviewCount ? "Aktion erforderlich" : "alles sauber" }}</span>
            </div>
          </article>
        </section>

        <section class="workspace-grid">
          <v-card dark flat class="admin-panel settings-panel">
            <div class="panel-heading">
              <div class="panel-title">
                <span class="panel-icon">
                  <v-icon size="21">mdi-tune-variant</v-icon>
                </span>
                <div>
                  <small>EVENT SETUP</small>
                  <h2>Einstellungen</h2>
                </div>
              </div>
              <span class="unsaved-hint"> Änderungen werden erst beim Speichern aktiv </span>
            </div>

            <div class="launch-control" :class="{ enabled: settings.registrationOpen }">
              <div class="launch-copy">
                <span class="launch-light"></span>
                <div>
                  <strong>
                    {{ settings.registrationOpen ? "Online-Anmeldung freigeben" : "Online-Anmeldung geschlossen" }}
                  </strong>
                  <small> Erst nach Rogers Freigabe für die Kunden öffnen. </small>
                </div>
              </div>
              <v-switch v-model="settings.registrationOpen" color="#f4d35e" inset hide-details aria-label="Online-Anmeldung umschalten" />
            </div>

            <div class="launch-control homepage-control" :class="{ enabled: settings.showOnHomepage }">
              <div class="launch-copy">
                <span class="launch-light"></span>
                <div>
                  <strong>
                    {{ settings.showOnHomepage ? "Auf Startseite sichtbar" : "Auf Startseite ausgeblendet" }}
                  </strong>
                  <small> Steuert nur den Event-Teaser; die Online-Anmeldung bleibt separat. </small>
                </div>
              </div>
              <v-switch v-model="settings.showOnHomepage" color="#f4d35e" inset hide-details aria-label="Startseiten-Teaser umschalten" />
            </div>

            <div class="settings-fields">
              <v-text-field v-model="settings.name" label="Eventname" outlined dense hide-details="auto" class="field-span-2" />
              <v-text-field v-model="settings.registrationOpensAt" label="Anmeldestart" type="datetime-local" outlined dense hide-details="auto" />
              <v-text-field v-model="settings.registrationClosesAt" label="Anmeldeschluss" type="datetime-local" outlined dense hide-details="auto" />
              <v-text-field v-model="settings.locationName" label="Austragungsort" outlined dense hide-details="auto" />
              <v-text-field
                v-model="settings.locationAddress"
                label="Adresse"
                placeholder="Vor dem Livegang ergänzen"
                outlined
                dense
                hide-details="auto" />
              <v-text-field v-model="settings.contactEmail" label="Kontakt-E-Mail" outlined dense hide-details="auto" />
              <v-text-field v-model="settings.contactPhone" label="Kontakttelefon" outlined dense hide-details="auto" />
              <v-text-field
                v-model="settings.shirtSizes"
                label="Unisex-T-Shirt-Grössen, kommagetrennt"
                placeholder="XS, S, M, L, XL, XXL"
                outlined
                dense
                hide-details="auto"
                class="field-span-2" />
            </div>

            <div class="settings-footer">
              <div>
                <v-icon size="17">mdi-shield-check-outline</v-icon>
                <span>Änderungen gelten sofort nach dem Speichern.</span>
              </div>
              <v-btn class="admin-primary" large :loading="savingSettings" @click="saveSettings">
                Einstellungen speichern
                <v-icon right size="19">mdi-check</v-icon>
              </v-btn>
            </div>
          </v-card>

          <div class="side-stack">
            <v-card dark flat class="admin-panel status-panel">
              <div class="panel-heading compact-heading">
                <div class="panel-title">
                  <span class="panel-icon">
                    <v-icon size="21">mdi-access-point</v-icon>
                  </span>
                  <div>
                    <small>LIVE STATUS</small>
                    <h2>Registration</h2>
                  </div>
                </div>
              </div>
              <div class="registration-signal" :class="{ online: event.registrationOpen }">
                <span class="signal-orbit"><i></i></span>
                <div>
                  <strong>{{ event.registrationOpen ? "ONLINE" : "OFFLINE" }}</strong>
                  <small>{{ registrationStatusCopy }}</small>
                </div>
              </div>
              <div class="status-checks">
                <div :class="{ done: !!event.locationName }">
                  <v-icon size="18">
                    {{ event.locationName ? "mdi-check-circle" : "mdi-circle-outline" }}
                  </v-icon>
                  Austragungsort gesetzt
                </div>
                <div :class="{ done: !!event.contactEmail }">
                  <v-icon size="18">
                    {{ event.contactEmail ? "mdi-check-circle" : "mdi-circle-outline" }}
                  </v-icon>
                  Kontakt hinterlegt
                </div>
              </div>
            </v-card>

            <v-card dark flat class="admin-panel shirts-panel">
              <div class="panel-heading compact-heading">
                <div class="panel-title">
                  <span class="panel-icon">
                    <v-icon size="21">mdi-tshirt-crew-outline</v-icon>
                  </span>
                  <div>
                    <small>MERCHANDISE</small>
                    <h2>Unisex-Shirt-Bedarf</h2>
                  </div>
                </div>
                <span class="shirt-total">{{ shirtTotal }} TOTAL</span>
              </div>

              <div v-if="shirtEntries.length" class="shirt-list">
                <div v-for="entry in shirtEntries" :key="entry.size" class="shirt-row">
                  <span class="shirt-size">{{ entry.size }}</span>
                  <div class="shirt-bar">
                    <i :style="{ width: `${entry.percent}%` }"></i>
                  </div>
                  <strong>{{ entry.amount }}</strong>
                </div>
              </div>
              <div v-else class="empty-compact">
                <v-icon size="27">mdi-tshirt-crew-outline</v-icon>
                <span>Noch keine aktiven, bezahlten Unisex-Shirts.</span>
              </div>
            </v-card>
          </div>
        </section>

        <v-card dark flat class="admin-panel capacity-panel">
          <div class="panel-heading">
            <div class="panel-title">
              <span class="panel-icon">
                <v-icon size="21">mdi-chart-donut</v-icon>
              </span>
              <div>
                <small>RACE CAPACITY</small>
                <h2>Kategorie-Kontingente</h2>
              </div>
            </div>
            <span class="unsaved-hint"> Bezahlte Plätze und laufende Zahlungen sind eingerechnet </span>
          </div>

          <div class="capacity-grid">
            <article v-for="category in event.categories" :key="category.key" class="capacity-card" :class="{ full: category.soldOut }">
              <div class="capacity-head">
                <span class="capacity-category-icon">
                  <v-icon size="20">
                    {{ category.type === "double" ? "mdi-account-multiple" : "mdi-run-fast" }}
                  </v-icon>
                </span>
                <div>
                  <strong>{{ category.label }}</strong>
                  <small> {{ category.briefingTime }} Briefing · {{ category.startTime }} Start </small>
                </div>
                <span class="capacity-state" :class="{ full: category.soldOut }">
                  {{ category.soldOut ? "VOLL" : "OFFEN" }}
                </span>
              </div>

              <div class="capacity-numbers">
                <div>
                  <strong>{{ category.occupiedParticipants }}</strong>
                  <span>/ {{ category.capacityParticipants }}</span>
                </div>
                <small>
                  {{ Math.max(0, category.capacityParticipants - category.occupiedParticipants) }}
                  Personen frei
                </small>
              </div>
              <div class="capacity-track">
                <i :style="{ width: `${capacityPercent(category)}%` }"></i>
              </div>
              <div class="capacity-foot">
                <span v-if="category.type === 'double'">
                  {{ Math.floor(category.occupiedParticipants / 2) }} / {{ Math.floor(category.capacityParticipants / 2) }} Teams
                </span>
                <span v-else> {{ Math.round(capacityPercent(category)) }}% belegt </span>
                <v-btn
                  text
                  small
                  class="capacity-action"
                  :loading="capacitySaving === category.key"
                  :disabled="cannotToggleCapacity(category)"
                  @click="toggleCapacity(category)">
                  {{
                    category.capacityParticipants < category.maxCapacityParticipants
                      ? `Auf ${category.maxCapacityParticipants} erweitern`
                      : `Auf ${category.initialCapacityParticipants} reduzieren`
                  }}
                  <v-icon right size="16">mdi-arrow-right</v-icon>
                </v-btn>
              </div>
            </article>
          </div>
        </v-card>

        <v-card dark flat class="admin-panel registrations-panel">
          <div class="registrations-heading">
            <div class="panel-title">
              <span class="panel-icon">
                <v-icon size="21">mdi-format-list-bulleted</v-icon>
              </span>
              <div>
                <small>PARTICIPANTS</small>
                <h2>Anmeldungen</h2>
              </div>
            </div>
            <span class="result-count"> {{ filteredRegistrations.length }} von {{ registrations.length }} </span>
          </div>

          <div class="filter-bar">
            <v-text-field
              v-model.trim="search"
              prepend-inner-icon="mdi-magnify"
              label="Name oder E-Mail suchen"
              outlined
              dense
              clearable
              hide-details />
            <v-select
              v-model="categoryFilter"
              :items="categoryFilterItems"
              item-text="label"
              item-value="key"
              label="Kategorie"
              outlined
              dense
              hide-details />
            <v-select
              v-model="statusFilter"
              :items="statusFilterItems"
              item-text="label"
              item-value="key"
              label="Status"
              outlined
              dense
              hide-details />
            <button type="button" class="filter-export" @click="exportCsv">
              <v-icon size="18">mdi-file-export-outline</v-icon>
              Exportieren
            </button>
          </div>

          <div class="desktop-registration-table">
            <v-data-table :headers="headers" :items="filteredRegistrations" :loading="loading" :items-per-page="20" class="registration-table" dark>
              <template v-slot:item.registeredAt="{ item }">
                <div class="date-cell">
                  <strong>{{ formatShortDate(item.registeredAt) }}</strong>
                  <span>{{ formatShortTime(item.registeredAt) }}</span>
                </div>
              </template>

              <template v-slot:item.categoryLabel="{ item }">
                <span class="category-cell">{{ item.categoryLabel }}</span>
              </template>

              <template v-slot:item.participants="{ item }">
                <div class="table-participants">
                  <div v-for="participant in item.participants" :key="participant.email" class="table-person">
                    <span class="person-avatar">
                      {{ participantInitials(participant) }}
                    </span>
                    <div>
                      <strong> {{ participant.firstName }} {{ participant.lastName }} </strong>
                      <small>{{ participant.email }}</small>
                    </div>
                    <span
                      class="member-dot"
                      :class="{ active: participant.isMember }"
                      :title="participant.isMember ? 'Mitglied' : 'Nichtmitglied'"></span>
                    <span class="shirt-chip">{{ participant.shirtSize }}</span>
                  </div>
                </div>
              </template>

              <template v-slot:item.estimatedTime="{ item }">
                <span class="time-cell">
                  <v-icon size="16">mdi-timer-outline</v-icon>
                  {{ item.estimatedTime }}
                </span>
              </template>

              <template v-slot:item.totalFee="{ item }">
                <div class="fee-stack">
                  <strong class="fee-cell">
                    {{ item.registrationSource === "admin_manual" ? "GRATIS" : `CHF ${Number(item.totalFee).toFixed(2)}` }}
                  </strong>
                  <span v-if="item.registrationSource === 'admin_manual'" class="manual-badge"> ADMIN </span>
                </div>
              </template>

              <template v-slot:item.paymentStatus="{ item }">
                <span class="status-badge" :class="`payment-${item.paymentStatus}`">
                  {{ paymentLabel(item.paymentStatus, item) }}
                </span>
              </template>

              <template v-slot:item.status="{ item }">
                <span class="status-badge" :class="`registration-${item.status}`">
                  {{ statusLabel(item.status) }}
                </span>
              </template>

              <template v-slot:item.actions="{ item }">
                <v-menu offset-y left dark content-class="admin-action-menu">
                  <template v-slot:activator="{ on, attrs }">
                    <v-btn icon small class="more-action" v-bind="attrs" v-on="on">
                      <v-icon size="20">mdi-dots-horizontal</v-icon>
                    </v-btn>
                  </template>
                  <v-list dense>
                    <v-list-item v-if="item.status !== 'cancelled'" @click="updateStatus(item, { status: 'cancelled' })">
                      <v-list-item-icon>
                        <v-icon size="18">mdi-close-circle-outline</v-icon>
                      </v-list-item-icon>
                      <v-list-item-title> Anmeldung stornieren </v-list-item-title>
                    </v-list-item>
                    <v-list-item v-else @click="updateStatus(item, { status: 'active' })">
                      <v-list-item-icon>
                        <v-icon size="18">mdi-backup-restore</v-icon>
                      </v-list-item-icon>
                      <v-list-item-title> Anmeldung reaktivieren </v-list-item-title>
                    </v-list-item>
                    <v-list-item
                      v-if="item.paymentStatus !== 'paid'"
                      @click="
                        updateStatus(item, {
                          paymentStatus: 'paid',
                          status: 'active',
                        })
                      ">
                      <v-list-item-icon>
                        <v-icon size="18">mdi-cash-check</v-icon>
                      </v-list-item-icon>
                      <v-list-item-title> Als bezahlt markieren </v-list-item-title>
                    </v-list-item>
                    <v-divider v-if="item.registrationSource !== 'admin_manual' && item.paymentStatus === 'paid'" />
                    <v-list-item
                      v-if="item.registrationSource !== 'admin_manual' && item.paymentStatus === 'paid'"
                      @click="openPaymentConfirmation(item, 'unpaid')">
                      <v-list-item-icon>
                        <v-icon size="18">mdi-cash-remove</v-icon>
                      </v-list-item-icon>
                      <v-list-item-title> Als unbezahlt markieren </v-list-item-title>
                    </v-list-item>
                    <v-list-item
                      v-if="item.registrationSource !== 'admin_manual' && item.paymentStatus === 'paid'"
                      @click="openPaymentConfirmation(item, 'refunded')">
                      <v-list-item-icon>
                        <v-icon size="18">mdi-cash-refund</v-icon>
                      </v-list-item-icon>
                      <v-list-item-title> Als erstattet markieren </v-list-item-title>
                    </v-list-item>
                  </v-list>
                </v-menu>
              </template>

              <template v-slot:no-data>
                <div class="table-empty">
                  <v-icon size="31">mdi-account-search-outline</v-icon>
                  <span>Keine passenden Anmeldungen gefunden.</span>
                </div>
              </template>
            </v-data-table>
          </div>

          <div class="mobile-registration-list">
            <article v-for="item in filteredRegistrations" :key="item._id" class="mobile-registration-card">
              <div class="mobile-registration-head">
                <div>
                  <small>{{ formatDateTime(item.registeredAt) }}</small>
                  <strong>{{ item.categoryLabel }}</strong>
                </div>
                <v-menu offset-y left dark>
                  <template v-slot:activator="{ on, attrs }">
                    <v-btn icon class="more-action" v-bind="attrs" v-on="on">
                      <v-icon>mdi-dots-horizontal</v-icon>
                    </v-btn>
                  </template>
                  <v-list dense>
                    <v-list-item v-if="item.status !== 'cancelled'" @click="updateStatus(item, { status: 'cancelled' })">
                      <v-list-item-title> Anmeldung stornieren </v-list-item-title>
                    </v-list-item>
                    <v-list-item v-else @click="updateStatus(item, { status: 'active' })">
                      <v-list-item-title> Anmeldung reaktivieren </v-list-item-title>
                    </v-list-item>
                    <v-list-item
                      v-if="item.paymentStatus !== 'paid'"
                      @click="
                        updateStatus(item, {
                          paymentStatus: 'paid',
                          status: 'active',
                        })
                      ">
                      <v-list-item-title> Als bezahlt markieren </v-list-item-title>
                    </v-list-item>
                    <v-divider v-if="item.registrationSource !== 'admin_manual' && item.paymentStatus === 'paid'" />
                    <v-list-item
                      v-if="item.registrationSource !== 'admin_manual' && item.paymentStatus === 'paid'"
                      @click="openPaymentConfirmation(item, 'unpaid')">
                      <v-list-item-title> Als unbezahlt markieren </v-list-item-title>
                    </v-list-item>
                    <v-list-item
                      v-if="item.registrationSource !== 'admin_manual' && item.paymentStatus === 'paid'"
                      @click="openPaymentConfirmation(item, 'refunded')">
                      <v-list-item-title> Als erstattet markieren </v-list-item-title>
                    </v-list-item>
                  </v-list>
                </v-menu>
              </div>

              <div class="mobile-participants">
                <div v-for="participant in item.participants" :key="participant.email" class="mobile-person">
                  <span class="person-avatar">
                    {{ participantInitials(participant) }}
                  </span>
                  <div>
                    <strong> {{ participant.firstName }} {{ participant.lastName }} </strong>
                    <small>{{ participant.email }}</small>
                  </div>
                  <span class="shirt-chip"> SHIRT {{ participant.shirtSize }} </span>
                </div>
              </div>

              <div class="mobile-registration-meta">
                <div>
                  <small>ZIELZEIT</small>
                  <strong>{{ item.estimatedTime }}</strong>
                </div>
                <div>
                  <small>BETRAG</small>
                  <strong>
                    {{ item.registrationSource === "admin_manual" ? "GRATIS" : `CHF ${Number(item.totalFee).toFixed(2)}` }}
                  </strong>
                </div>
                <div>
                  <small>ZAHLUNG</small>
                  <span class="status-badge" :class="`payment-${item.paymentStatus}`">
                    {{ paymentLabel(item.paymentStatus, item) }}
                  </span>
                </div>
                <div>
                  <small>STATUS</small>
                  <span class="status-badge" :class="`registration-${item.status}`">
                    {{ statusLabel(item.status) }}
                  </span>
                </div>
              </div>
            </article>

            <div v-if="!filteredRegistrations.length" class="mobile-empty">
              <v-icon size="34">mdi-account-search-outline</v-icon>
              <strong>Keine Anmeldungen gefunden</strong>
              <span>Passe Suche oder Kategorie-Filter an.</span>
            </div>
          </div>
        </v-card>
      </template>
    </main>

    <v-dialog v-model="paymentConfirmation.show" max-width="520" persistent content-class="payment-confirmation-dialog">
      <v-card dark flat class="payment-confirmation-card">
        <div class="payment-confirmation-icon" :class="{ refund: paymentConfirmation.status === 'refunded' }">
          <v-icon size="28">
            {{ paymentConfirmation.status === "refunded" ? "mdi-cash-refund" : "mdi-cash-remove" }}
          </v-icon>
        </div>
        <p class="payment-confirmation-eyebrow">Zahlungsstatus</p>
        <h2>{{ paymentConfirmationCopy.title }}</h2>
        <p>{{ paymentConfirmationCopy.message }}</p>
        <div class="payment-confirmation-actions">
          <button type="button" class="manual-cancel" :disabled="paymentConfirmation.saving" @click="closePaymentConfirmation">Abbrechen</button>
          <v-btn class="admin-primary" large :loading="paymentConfirmation.saving" @click="confirmPaymentStatus">
            {{ paymentConfirmationCopy.action }}
          </v-btn>
        </div>
      </v-card>
    </v-dialog>

    <v-dialog v-model="manualRegistration.show" max-width="760" persistent scrollable content-class="manual-registration-dialog">
      <v-card dark flat class="manual-dialog-card">
        <div class="manual-dialog-header">
          <div class="manual-dialog-title">
            <span>
              <v-icon size="23">mdi-account-plus-outline</v-icon>
            </span>
            <div>
              <small>ADMIN SIGNUP</small>
              <h2>Instruktor kostenlos anmelden</h2>
            </div>
          </div>
          <v-btn icon class="manual-close" :disabled="manualRegistration.saving" aria-label="Dialog schliessen" @click="closeManualRegistration">
            <v-icon size="21">mdi-close</v-icon>
          </v-btn>
        </div>

        <v-card-text class="manual-dialog-body">
          <div class="manual-info">
            <v-icon size="19">mdi-shield-account-outline</v-icon>
            <span>
              Nur für Instruktoren mit aktiver Mitgliedschaft. Die Anmeldung ist sofort aktiv, kostet CHF 0.00 und zählt gegen das Kontingent.
            </span>
          </div>

          <v-form ref="manualRegistrationForm" v-model="manualFormValid" lazy-validation>
            <section class="manual-form-section">
              <div class="manual-section-heading">
                <span>01</span>
                <div>
                  <strong>App-Mitglied auswählen</strong>
                  <small>Diese Person wird als Anmelder hinterlegt.</small>
                </div>
              </div>

              <user-search
                :key="manualSearchKey"
                class="manual-user-search"
                rights="mk_hyroxPFT"
                label="Mitglied nach Name oder E-Mail suchen"
                @update="onManualUserSelected" />

              <v-slide-y-transition>
                <div v-if="manualRegistration.loadingUser" class="manual-user-loading">
                  <v-progress-circular indeterminate color="#f4d35e" size="22" width="2" />
                  Mitgliedsdaten werden geladen…
                </div>
                <div v-else-if="manualRegistration.selectedUser" class="manual-selected-user">
                  <span class="manual-user-avatar">
                    {{ selectedManualUserInitials }}
                  </span>
                  <div>
                    <strong>
                      {{ manualRegistration.selectedUser.vorname }}
                      {{ manualRegistration.selectedUser.nachname }}
                    </strong>
                    <small>{{ manualRegistration.selectedUser.email }}</small>
                  </div>
                  <span class="manual-member-pill">APP-MITGLIED</span>
                </div>
              </v-slide-y-transition>
            </section>

            <section class="manual-form-section">
              <div class="manual-section-heading">
                <span>02</span>
                <div>
                  <strong>Race-Details</strong>
                  <small>Kategorie, Unisex-Shirt und erwartete Zielzeit.</small>
                </div>
              </div>

              <div class="manual-field-grid">
                <v-select
                  v-model="manualRegistration.categoryKey"
                  :items="manualCategoryItems"
                  item-text="label"
                  item-value="key"
                  item-disabled="disabled"
                  :rules="[requiredRule]"
                  label="Kategorie"
                  outlined
                  dense
                  hide-details="auto"
                  class="manual-field-span-2" />
                <v-select
                  v-model="manualRegistration.mainShirtSize"
                  :items="event.shirtSizes"
                  :rules="[requiredRule]"
                  label="Unisex-T-Shirt-Grösse"
                  outlined
                  dense
                  hide-details="auto" />
                <v-text-field
                  v-model.trim="manualRegistration.estimatedTime"
                  :rules="[requiredRule, estimatedTimeRule]"
                  label="Geschätzte Zielzeit"
                  placeholder="01:20"
                  suffix="Std."
                  outlined
                  dense
                  hide-details="auto" />
              </div>
            </section>

            <v-expand-transition>
              <section v-if="manualIsDouble" class="manual-form-section">
                <div class="manual-section-heading">
                  <span>03</span>
                  <div>
                    <strong>Double-Partner</strong>
                    <small> Beide Personen werden kostenlos als Instruktoren angemeldet. </small>
                  </div>
                </div>

                <div class="manual-field-grid">
                  <v-text-field
                    v-model.trim="manualRegistration.partner.firstName"
                    :rules="[requiredRule]"
                    label="Vorname"
                    outlined
                    dense
                    hide-details="auto" />
                  <v-text-field
                    v-model.trim="manualRegistration.partner.lastName"
                    :rules="[requiredRule]"
                    label="Nachname"
                    outlined
                    dense
                    hide-details="auto" />
                  <v-text-field
                    v-model.trim="manualRegistration.partner.email"
                    :rules="[requiredRule, emailRule]"
                    label="E-Mail"
                    type="email"
                    outlined
                    dense
                    hide-details="auto" />
                  <v-text-field
                    v-model="manualRegistration.partner.birthdate"
                    :rules="[requiredRule]"
                    label="Geburtsdatum"
                    type="date"
                    outlined
                    dense
                    hide-details="auto" />
                  <v-select
                    v-model="manualRegistration.partner.shirtSize"
                    :items="event.shirtSizes"
                    :rules="[requiredRule]"
                    label="Unisex-T-Shirt-Grösse Partner"
                    outlined
                    dense
                    hide-details="auto"
                    class="manual-field-span-2" />
                </div>
              </section>
            </v-expand-transition>
          </v-form>

          <v-alert v-if="manualRegistration.error" type="error" text dense class="manual-alert">
            {{ manualRegistration.error }}
          </v-alert>
        </v-card-text>

        <div class="manual-dialog-footer">
          <button type="button" class="manual-cancel" :disabled="manualRegistration.saving" @click="closeManualRegistration">Abbrechen</button>
          <v-btn
            class="admin-primary manual-submit"
            large
            :loading="manualRegistration.saving"
            :disabled="!manualCanSubmit"
            @click="submitManualRegistration">
            Kostenlos anmelden
            <v-icon right size="19">mdi-check</v-icon>
          </v-btn>
        </div>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import api from "@/api"
import userSearch from "@/components/userSearch.vue"

function createManualRegistrationState(show = false) {
  return {
    show,
    loadingUser: false,
    saving: false,
    error: "",
    selectedUserId: null,
    selectedUser: null,
    categoryKey: "",
    mainShirtSize: "",
    estimatedTime: "",
    partner: {
      firstName: "",
      lastName: "",
      email: "",
      birthdate: "",
      shirtSize: "",
    },
  }
}

export default {
  name: "HyroxSimulationAdmin",
  components: {
    userSearch,
  },
  data() {
    return {
      loading: true,
      savingSettings: false,
      capacitySaving: "",
      event: null,
      registrations: [],
      stats: {
        activeRegistrations: 0,
        activeParticipants: 0,
        totalPaidRevenue: 0,
        shirtSummary: {},
      },
      settings: {
        registrationOpen: false,
        showOnHomepage: false,
        registrationOpensAt: "",
        registrationClosesAt: "",
        name: "",
        locationName: "",
        locationAddress: "",
        contactEmail: "",
        contactPhone: "",
        shirtSizes: "",
      },
      search: "",
      categoryFilter: "all",
      statusFilter: "active",
      manualFormValid: false,
      manualSearchKey: 0,
      manualRegistration: createManualRegistrationState(),
      paymentConfirmation: {
        show: false,
        item: null,
        status: "",
        saving: false,
      },
      requiredRule: (value) => Boolean(value) || "Pflichtfeld",
      emailRule: (value) => /.+@.+\..+/.test(String(value || "").trim()) || "Ungültige E-Mail-Adresse",
      estimatedTimeRule: (value) => /^(\d{1,2}):([0-5]\d)$/.test(String(value || "").trim()) || "Format HH:MM verwenden",
      headers: [
        { text: "Datum", value: "registeredAt", width: "105px" },
        { text: "Kategorie", value: "categoryLabel", width: "145px" },
        {
          text: "Teilnehmer",
          value: "participants",
          sortable: false,
          width: "300px",
        },
        { text: "Schätzung", value: "estimatedTime", width: "105px" },
        { text: "Gebühr", value: "totalFee", width: "110px" },
        { text: "Zahlung", value: "paymentStatus", width: "105px" },
        { text: "Status", value: "status", width: "115px" },
        { text: "", value: "actions", sortable: false, width: "50px" },
      ],
    }
  },
  computed: {
    manualCategoryItems() {
      if (!this.event) return []
      return this.event.categories
        .filter((category) => category.enabled)
        .map((category) => {
          const remaining = category.type === "double" ? category.remainingRegistrations : category.remainingParticipants
          const unit = category.type === "double" ? "Teams" : "Plätze"
          return {
            key: category.key,
            label: `${category.label} · ${category.startTime} Uhr · ${remaining} ${unit} frei`,
            disabled: category.soldOut,
          }
        })
    },
    manualSelectedCategory() {
      if (!this.event) return null
      return this.event.categories.find((category) => category.key === this.manualRegistration.categoryKey) || null
    },
    manualIsDouble() {
      return Boolean(this.manualSelectedCategory && this.manualSelectedCategory.type === "double")
    },
    selectedManualUserInitials() {
      const user = this.manualRegistration.selectedUser
      if (!user) return "–"
      return `${String(user.vorname || "").charAt(0)}${String(user.nachname || "").charAt(0)}`.toUpperCase()
    },
    manualCanSubmit() {
      const registration = this.manualRegistration
      const hasBaseFields = Boolean(
        registration.selectedUserId &&
          registration.selectedUser &&
          !registration.loadingUser &&
          registration.categoryKey &&
          registration.mainShirtSize &&
          /^(\d{1,2}):([0-5]\d)$/.test(registration.estimatedTime || ""),
      )
      if (!hasBaseFields) return false
      if (!this.manualIsDouble) return true
      return Boolean(
        registration.partner.firstName &&
          registration.partner.lastName &&
          /.+@.+\..+/.test(registration.partner.email || "") &&
          registration.partner.birthdate &&
          registration.partner.shirtSize,
      )
    },
    categoryFilterItems() {
      return [{ key: "all", label: "Alle Kategorien" }, ...(this.event ? this.event.categories : [])]
    },
    statusFilterItems() {
      return [
        { key: "active", label: "Aktive Buchungen" },
        { key: "pending", label: "Zahlung offen" },
        { key: "inactive", label: "Storniert / abgelaufen" },
        { key: "all", label: "Alle Anmeldungen" },
      ]
    },
    filteredRegistrations() {
      const query = String(this.search || "")
        .trim()
        .toLowerCase()
      return this.registrations.filter((registration) => {
        const matchesStatus =
          this.statusFilter === "all" ||
          (this.statusFilter === "active" && ["active", "payment_review"].includes(registration.status)) ||
          (this.statusFilter === "pending" && registration.status === "pending_payment") ||
          (this.statusFilter === "inactive" && ["cancelled", "expired"].includes(registration.status))
        if (!matchesStatus) return false

        if (this.categoryFilter !== "all" && registration.categoryKey !== this.categoryFilter) {
          return false
        }
        if (!query) return true

        const participantSearch = (registration.participants || [])
          .map((participant) => [participant.firstName, participant.lastName, participant.email].filter(Boolean).join(" "))
          .join(" ")
        return `${registration.categoryLabel || ""} ${participantSearch}`.toLowerCase().includes(query)
      })
    },
    eventDateLabel() {
      if (!this.event || !this.event.eventDate) return "EVENT"
      const date = new Date(this.event.eventDate)
      if (Number.isNaN(date.getTime())) return "EVENT"
      return new Intl.DateTimeFormat("en-GB", {
        day: "2-digit",
        month: "short",
        year: "numeric",
      })
        .format(date)
        .toUpperCase()
    },
    capacityTotal() {
      if (!this.event) return 0
      return this.event.categories.reduce((sum, category) => sum + Number(category.capacityParticipants || 0), 0)
    },
    capacityOccupied() {
      if (!this.event) return 0
      return this.event.categories.reduce((sum, category) => sum + Number(category.occupiedParticipants || 0), 0)
    },
    capacityRemaining() {
      return Math.max(0, this.capacityTotal - this.capacityOccupied)
    },
    capacityUsage() {
      if (!this.capacityTotal) return 0
      return Math.min(100, (this.capacityOccupied / this.capacityTotal) * 100)
    },
    pendingCount() {
      return this.registrations.filter((registration) => registration.status === "pending_payment").length
    },
    reviewCount() {
      return this.registrations.filter((registration) => registration.status === "payment_review").length
    },
    shirtTotal() {
      return Object.values(this.stats.shirtSummary || {}).reduce((sum, amount) => sum + Number(amount || 0), 0)
    },
    shirtEntries() {
      const summary = this.stats.shirtSummary || {}
      const configuredSizes = this.event && this.event.shirtSizes ? this.event.shirtSizes : []
      const sizes = [...configuredSizes, ...Object.keys(summary).filter((size) => !configuredSizes.includes(size))]
      const maxAmount = Math.max(1, ...sizes.map((size) => Number(summary[size] || 0)))
      return sizes
        .map((size) => ({
          size,
          amount: Number(summary[size] || 0),
          percent: (Number(summary[size] || 0) / maxAmount) * 100,
        }))
        .filter((entry) => entry.amount > 0)
    },
    paymentConfirmationCopy() {
      if (this.paymentConfirmation.status === "refunded") {
        return {
          title: "Rückerstattung bestätigen?",
          message:
            "Bestätige dies nur, wenn der Betrag bereits in Adyen zurückerstattet wurde. Diese Aktion löst keine Rückerstattung aus. Die Anmeldung wird gleichzeitig storniert und aus dem Shirt-Bedarf entfernt.",
          action: "Als erstattet markieren",
        }
      }
      return {
        title: "Zahlung als unbezahlt markieren?",
        message: "Damit änderst du nur den internen Zahlungsstatus. Eine bereits ausgeführte Adyen-Zahlung wird dadurch nicht rückgängig gemacht.",
        action: "Als unbezahlt markieren",
      }
    },
    registrationStatusCopy() {
      if (this.event && this.event.registrationOpen) {
        return `Geöffnet bis ${this.formatDateTime(this.event.registrationClosesAt)}`
      }
      if (this.settings.registrationOpen) {
        return "Ausserhalb des konfigurierten Zeitfensters"
      }
      return "Manuell geschlossen"
    },
  },
  async created() {
    await this.fetchData()
  },
  methods: {
    toDateTimeLocal(value) {
      if (!value) return ""
      const date = new Date(value)
      const offset = date.getTimezoneOffset() * 60000
      return new Date(date.getTime() - offset).toISOString().slice(0, 16)
    },
    formatDateTime(value) {
      if (!value) return ""
      return new Date(value).toLocaleString("de-CH", {
        day: "2-digit",
        month: "2-digit",
        year: "2-digit",
        hour: "2-digit",
        minute: "2-digit",
      })
    },
    formatShortDate(value) {
      if (!value) return ""
      return new Date(value).toLocaleDateString("de-CH", {
        day: "2-digit",
        month: "short",
      })
    },
    formatShortTime(value) {
      if (!value) return ""
      return new Date(value).toLocaleTimeString("de-CH", {
        hour: "2-digit",
        minute: "2-digit",
      })
    },
    participantInitials(participant) {
      if (!participant) return "–"
      return `${String(participant.firstName || "").charAt(0)}${String(participant.lastName || "").charAt(0)}`.toUpperCase()
    },
    openManualRegistration() {
      this.manualRegistration = createManualRegistrationState(true)
      this.manualSearchKey += 1
      this.manualFormValid = false
      this.$nextTick(() => {
        if (this.$refs.manualRegistrationForm) {
          this.$refs.manualRegistrationForm.resetValidation()
        }
      })
    },
    closeManualRegistration() {
      if (this.manualRegistration.saving) return
      this.manualRegistration.show = false
    },
    async onManualUserSelected(value) {
      const userId = value && typeof value === "object" ? value._id : value
      this.manualRegistration.error = ""
      this.manualRegistration.selectedUserId = userId || null
      this.manualRegistration.selectedUser = null
      if (!userId) return

      this.manualRegistration.loadingUser = true
      try {
        this.manualRegistration.selectedUser = await api.hyroxSimulation.admin.fetchUserDetails(userId)
      } catch (error) {
        this.manualRegistration.selectedUserId = null
        this.manualRegistration.error = error.response?.data || "Mitgliedsdaten konnten nicht geladen werden."
      } finally {
        this.manualRegistration.loadingUser = false
      }
    },
    async submitManualRegistration() {
      this.manualRegistration.error = ""
      if (!this.manualRegistration.selectedUserId) {
        this.manualRegistration.error = "Bitte wähle zuerst ein App-Mitglied aus."
        return
      }
      if (!this.$refs.manualRegistrationForm || !this.$refs.manualRegistrationForm.validate()) {
        this.manualRegistration.error = "Bitte prüfe die markierten Pflichtfelder."
        return
      }

      this.manualRegistration.saving = true
      try {
        const partner = this.manualRegistration.partner
        await api.hyroxSimulation.admin.manualRegister({
          userId: this.manualRegistration.selectedUserId,
          categoryKey: this.manualRegistration.categoryKey,
          mainShirtSize: this.manualRegistration.mainShirtSize,
          estimatedTime: this.manualRegistration.estimatedTime,
          partnerFirstName: this.manualIsDouble ? partner.firstName : "",
          partnerLastName: this.manualIsDouble ? partner.lastName : "",
          partnerEmail: this.manualIsDouble ? partner.email : "",
          partnerBirthdate: this.manualIsDouble ? partner.birthdate : "",
          partnerShirtSize: this.manualIsDouble ? partner.shirtSize : "",
        })
        this.manualRegistration.show = false
        this.$toast.success("Instruktorenanmeldung wurde kostenlos und aktiv eingetragen.")
        await this.fetchData()
      } catch (error) {
        this.manualRegistration.error = error.response?.data || "Die kostenlose Anmeldung konnte nicht erstellt werden."
      } finally {
        this.manualRegistration.saving = false
      }
    },
    openRegistrationPreview() {
      this.$router.push("/hyrox-simulation")
    },
    hydrateSettings() {
      this.settings = {
        registrationOpen: this.event.registrationOpenConfigured,
        showOnHomepage: Boolean(this.event.showOnHomepage),
        registrationOpensAt: this.toDateTimeLocal(this.event.registrationOpensAt),
        registrationClosesAt: this.toDateTimeLocal(this.event.registrationClosesAt),
        name: this.event.name,
        locationName: this.event.locationName,
        locationAddress: this.event.locationAddress || "",
        contactEmail: this.event.contactEmail,
        contactPhone: this.event.contactPhone,
        shirtSizes: this.event.shirtSizes.join(", "),
      }
    },
    async fetchData() {
      this.loading = true
      try {
        const response = await api.hyroxSimulation.admin.fetchRegistrations()
        this.event = response.event
        this.registrations = response.registrations || []
        this.stats = response.stats || this.stats
        this.hydrateSettings()
      } catch (error) {
        this.$toast.error(error.response?.data || "Admin-Daten konnten nicht geladen werden.")
      } finally {
        this.loading = false
      }
    },
    async saveSettings() {
      this.savingSettings = true
      try {
        const shirtSizes = this.settings.shirtSizes
          .split(",")
          .map((size) => size.trim())
          .filter(Boolean)
        await api.hyroxSimulation.admin.saveSettings({
          ...this.settings,
          shirtSizes,
          registrationOpensAt: new Date(this.settings.registrationOpensAt).toISOString(),
          registrationClosesAt: new Date(this.settings.registrationClosesAt).toISOString(),
        })
        this.$toast.success("Event-Einstellungen gespeichert.")
        await this.fetchData()
      } catch (error) {
        this.$toast.error(error.response?.data || "Einstellungen konnten nicht gespeichert werden.")
      } finally {
        this.savingSettings = false
      }
    },
    capacityPercent(category) {
      if (!category.capacityParticipants) return 0
      return Math.min(100, (category.occupiedParticipants / category.capacityParticipants) * 100)
    },
    cannotToggleCapacity(category) {
      return (
        category.capacityParticipants === category.maxCapacityParticipants && category.occupiedParticipants > category.initialCapacityParticipants
      )
    },
    async toggleCapacity(category) {
      const target =
        category.capacityParticipants < category.maxCapacityParticipants ? category.maxCapacityParticipants : category.initialCapacityParticipants
      this.capacitySaving = category.key
      try {
        await api.hyroxSimulation.admin.setCategoryCapacity(category.key, target)
        this.$toast.success("Kontingent aktualisiert.")
        await this.fetchData()
      } catch (error) {
        this.$toast.error(error.response?.data || "Kontingent konnte nicht geändert werden.")
      } finally {
        this.capacitySaving = ""
      }
    },
    paymentLabel(status, registration = null) {
      if (registration && registration.registrationSource === "admin_manual") {
        return "Entfällt"
      }
      return (
        {
          paid: "Bezahlt",
          unpaid: "Offen",
          failed: "Fehlgeschlagen",
          refunded: "Erstattet",
        }[status] || status
      )
    },
    statusLabel(status) {
      return (
        {
          active: "Aktiv",
          pending_payment: "Zahlung offen",
          cancelled: "Storniert",
          expired: "Abgelaufen",
          payment_review: "Prüfen",
        }[status] || status
      )
    },
    async updateStatus(item, update) {
      try {
        await api.hyroxSimulation.admin.updateStatus(item._id, update)
        this.$toast.success("Status aktualisiert.")
        await this.fetchData()
      } catch (error) {
        this.$toast.error(error.response?.data || "Status konnte nicht aktualisiert werden.")
      }
    },
    openPaymentConfirmation(item, status) {
      this.paymentConfirmation = {
        show: true,
        item,
        status,
        saving: false,
      }
    },
    closePaymentConfirmation() {
      if (this.paymentConfirmation.saving) return
      this.paymentConfirmation = {
        show: false,
        item: null,
        status: "",
        saving: false,
      }
    },
    async confirmPaymentStatus() {
      const item = this.paymentConfirmation.item
      const status = this.paymentConfirmation.status
      if (!item || !status) return

      this.paymentConfirmation.saving = true
      const update = { paymentStatus: status }
      if (status === "refunded") update.status = "cancelled"

      try {
        await api.hyroxSimulation.admin.updateStatus(item._id, update)
        this.$toast.success(status === "refunded" ? "Anmeldung storniert und als erstattet markiert." : "Zahlung als unbezahlt markiert.")
        this.paymentConfirmation = {
          show: false,
          item: null,
          status: "",
          saving: false,
        }
        await this.fetchData()
      } catch (error) {
        this.$toast.error(error.response?.data || "Zahlungsstatus konnte nicht aktualisiert werden.")
      } finally {
        this.paymentConfirmation.saving = false
      }
    },
    exportCsv() {
      const axios = require("axios")
      const exportParams = {
        scope: this.statusFilter,
      }
      if (this.categoryFilter !== "all") {
        exportParams.categoryKey = this.categoryFilter
      }
      if (String(this.search || "").trim()) {
        exportParams.search = String(this.search).trim()
      }
      const filenameSuffix =
        {
          active: "aktive_buchungen",
          pending: "offene_zahlungen",
          inactive: "storniert_abgelaufen",
          all: "alle_anmeldungen",
        }[this.statusFilter] || "aktive_buchungen"

      axios({
        url: api.hyroxSimulation.admin.exportCsvUrl(exportParams),
        method: "GET",
        headers: { Authorization: localStorage.getItem("token") },
        responseType: "blob",
      })
        .then((response) => {
          const link = document.createElement("a")
          const objectUrl = URL.createObjectURL(response.data)
          link.href = objectUrl
          link.download = `hyrox_simulation_2026_${filenameSuffix}.csv`
          document.body.appendChild(link)
          link.click()
          link.remove()
          setTimeout(() => URL.revokeObjectURL(objectUrl), 2000)
        })
        .catch(() => this.$toast.error("Export fehlgeschlagen."))
    },
  },
}
</script>

<style scoped>
.admin-shell {
  --ink: #f8f7f2;
  --muted: #909297;
  --surface: rgba(22, 23, 24, 0.94);
  --surface-soft: rgba(255, 255, 255, 0.035);
  --stroke: rgba(255, 255, 255, 0.09);
  --accent: #f4d35e;
  --accent-ink: #18170f;
  min-height: 100vh;
  font-size: 1rem;
  line-height: 1.5;
  overflow-x: hidden;
  position: relative;
  color: var(--ink);
  background: radial-gradient(circle at 8% -10%, rgba(244, 211, 94, 0.11), transparent 34rem),
    radial-gradient(circle at 100% 36%, rgba(77, 109, 245, 0.075), transparent 38rem), #0a0b0c;
  font-family: Inter, ui-sans-serif, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif;
}

.admin-ambient {
  position: fixed;
  z-index: 0;
  width: 440px;
  height: 440px;
  border-radius: 999px;
  filter: blur(125px);
  opacity: 0.08;
  pointer-events: none;
}

.admin-ambient-one {
  top: 12%;
  right: -240px;
  background: var(--accent);
}

.admin-ambient-two {
  bottom: -230px;
  left: -200px;
  background: #4c68ff;
}

.admin-topbar {
  position: sticky;
  top: 0;
  z-index: 40;
  display: grid;
  min-height: 70px;
  padding: 0 max(24px, calc((100vw - 1480px) / 2));
  grid-template-columns: 1fr auto 1fr;
  align-items: center;
  border-bottom: 1px solid rgba(255, 255, 255, 0.065);
  background: rgba(10, 11, 12, 0.78);
  backdrop-filter: blur(22px) saturate(140%);
  -webkit-backdrop-filter: blur(22px) saturate(140%);
}

.admin-brand {
  display: flex;
  align-items: center;
  gap: 10px;
}

.admin-brand-mark {
  display: grid;
  width: 38px;
  height: 38px;
  place-items: center;
  color: var(--accent-ink);
  border-radius: 11px;
  background: var(--accent);
  font-size: 0.75rem;
  font-weight: 950;
  letter-spacing: -0.025rem;
}

.admin-brand > div {
  display: flex;
  flex-direction: column;
}

.admin-brand strong {
  font-size: 0.75rem;
  font-weight: 950;
  letter-spacing: 0.0375rem;
}

.admin-brand small {
  color: #707277;
  font-size: 0.75rem;
  font-weight: 800;
  letter-spacing: 0.0875rem;
}

.live-status {
  display: inline-flex;
  min-height: 30px;
  padding: 0 11px;
  align-items: center;
  gap: 7px;
  color: #a2a3a6;
  border: 1px solid rgba(255, 255, 255, 0.09);
  border-radius: 999px;
  background: rgba(255, 255, 255, 0.035);
  font-size: 0.75rem;
  font-weight: 950;
  letter-spacing: 0.05625rem;
}

.live-status span {
  width: 7px;
  height: 7px;
  border-radius: 999px;
  background: #77797d;
}

.live-status.online {
  color: #7dd69c;
  border-color: rgba(74, 194, 116, 0.18);
  background: rgba(74, 194, 116, 0.06);
}

.live-status.online span {
  background: #65d28d;
  box-shadow: 0 0 0 4px rgba(74, 194, 116, 0.1);
}

.topbar-actions {
  display: flex;
  justify-self: end;
  gap: 8px;
}

.icon-action,
.manual-action,
.preview-action,
.export-action,
.filter-export {
  display: inline-flex;
  min-height: 40px;
  align-items: center;
  justify-content: center;
  color: #d2d2d0;
  border: 1px solid var(--stroke);
  background: rgba(255, 255, 255, 0.04);
  cursor: pointer;
  transition: border-color 180ms ease, background 180ms ease, transform 180ms ease;
}

.icon-action {
  width: 40px;
  padding: 0;
  border-radius: 12px;
}

.export-action {
  padding: 0 14px;
  gap: 7px;
  border-radius: 12px;
  font-size: 0.875rem;
  font-weight: 850;
}

.manual-action {
  padding: 0 14px;
  gap: 7px;
  color: var(--accent);
  border-color: rgba(244, 211, 94, 0.28);
  border-radius: 12px;
  background: rgba(244, 211, 94, 0.055);
  font-size: 0.875rem;
  font-weight: 900;
}

.manual-action .v-icon {
  color: var(--accent);
}

.icon-action,
.export-action {
  color: #171816;
  border-color: rgba(255, 255, 255, 0.92);
  background: #f7f7f3;
  box-shadow: 0 7px 18px rgba(0, 0, 0, 0.16);
}

.icon-action .v-icon,
.export-action .v-icon {
  color: #171816;
}

.preview-action {
  padding: 0 15px;
  gap: 7px;
  color: var(--accent-ink);
  border-color: var(--accent);
  border-radius: 12px;
  background: var(--accent);
  font-size: 0.875rem;
  font-weight: 950;
}

.preview-action .v-icon {
  color: var(--accent-ink);
}

.icon-action:hover,
.manual-action:hover,
.preview-action:hover,
.export-action:hover,
.filter-export:hover {
  border-color: rgba(244, 211, 94, 0.4);
  background: rgba(244, 211, 94, 0.06);
}

.preview-action:hover {
  color: var(--accent-ink);
  border-color: #ffe47f;
  background: #ffe47f;
  transform: translateY(-1px);
}

.manual-action:hover {
  color: var(--accent);
  border-color: rgba(244, 211, 94, 0.52);
  background: rgba(244, 211, 94, 0.095);
  transform: translateY(-1px);
}

.icon-action:hover,
.export-action:hover {
  color: #11120f;
  border-color: white;
  background: white;
  transform: translateY(-1px);
}

.icon-action:focus-visible,
.manual-action:focus-visible,
.preview-action:focus-visible,
.export-action:focus-visible,
.filter-export:focus-visible {
  outline: 3px solid rgba(244, 211, 94, 0.28);
  outline-offset: 2px;
}

.spinning {
  animation: admin-spin 900ms linear infinite;
}

@keyframes admin-spin {
  to {
    transform: rotate(360deg);
  }
}

.admin-container {
  position: relative;
  z-index: 1;
  width: min(1480px, calc(100% - 48px));
  margin: 0 auto;
  padding: 32px 0 80px;
}

.admin-loading {
  display: flex;
  min-height: 480px;
  align-items: center;
  justify-content: center;
  flex-direction: column;
  color: var(--muted);
}

.admin-loader {
  display: grid;
  width: 76px;
  height: 76px;
  margin-bottom: 18px;
  place-items: center;
  border: 1px solid rgba(244, 211, 94, 0.15);
  border-radius: 999px;
  background: rgba(244, 211, 94, 0.035);
}

.admin-loading strong {
  color: var(--ink);
  font-size: 1rem;
}

.admin-loading span {
  margin-top: 5px;
  font-size: 0.875rem;
}

.admin-hero {
  display: flex;
  min-height: 180px;
  padding: 32px 36px;
  align-items: center;
  justify-content: space-between;
  gap: 30px;
  overflow: hidden;
  position: relative;
  border: 1px solid var(--stroke);
  border-radius: 26px;
  background: linear-gradient(115deg, rgba(30, 31, 32, 0.97), rgba(17, 18, 19, 0.91)), #171819;
  box-shadow: 0 24px 70px rgba(0, 0, 0, 0.24);
}

.admin-hero::after {
  position: absolute;
  top: -190px;
  right: 7%;
  width: 360px;
  height: 360px;
  border: 1px solid rgba(244, 211, 94, 0.12);
  border-radius: 999px;
  content: "";
  box-shadow: 0 0 0 55px rgba(244, 211, 94, 0.015);
}

.admin-hero > div {
  position: relative;
  z-index: 1;
}

.hero-kicker {
  display: flex;
  margin-bottom: 11px;
  align-items: center;
  gap: 9px;
  color: #85878a;
  font-size: 0.75rem;
  font-weight: 900;
  letter-spacing: 0.075rem;
  text-transform: uppercase;
}

.hero-kicker span {
  color: var(--accent);
}

.hero-kicker i {
  width: 4px;
  height: 4px;
  border-radius: 999px;
  background: #626468;
}

.admin-hero h1 {
  max-width: 850px;
  margin: 0;
  font-size: 3rem;
  font-weight: 950;
  font-style: italic;
  line-height: 1;
  letter-spacing: -0.125rem;
  text-transform: uppercase;
}

.admin-hero p {
  margin: 12px 0 0;
  color: var(--muted);
  font-size: 1rem;
  line-height: 1.6;
}

.hero-health {
  display: flex;
  min-width: 260px;
  align-items: center;
  gap: 17px;
}

.health-ring {
  position: relative;
  display: grid;
  width: 88px;
  height: 88px;
  flex: 0 0 auto;
  place-items: center;
  border-radius: 999px;
  background: radial-gradient(circle closest-side, #18191a 78%, transparent 80% 100%),
    conic-gradient(var(--accent) var(--usage), rgba(255, 255, 255, 0.08) 0);
}

.health-ring strong,
.health-ring small {
  position: absolute;
}

.health-ring strong {
  margin-top: -8px;
  font-size: 1.1875rem;
  font-weight: 950;
}

.health-ring small {
  margin-top: 21px;
  color: #74767a;
  font-size: 0.75rem;
  font-weight: 900;
  letter-spacing: 0.05rem;
}

.health-copy {
  display: flex;
  flex-direction: column;
}

.health-copy small,
.metric-card small,
.panel-title small,
.mobile-registration-meta small {
  color: #737579;
  font-size: 0.75rem;
  font-weight: 900;
  letter-spacing: 0.06875rem;
}

.health-copy strong {
  margin: 3px 0;
  font-size: 1.375rem;
  font-weight: 950;
}

.health-copy span {
  color: #7fd39c;
  font-size: 0.875rem;
  font-weight: 750;
}

.metric-grid {
  display: grid;
  margin-top: 16px;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: 12px;
}

.metric-card {
  display: grid;
  min-height: 114px;
  padding: 19px;
  grid-template-columns: 43px minmax(0, 1fr);
  align-items: center;
  gap: 13px;
  border: 1px solid var(--stroke);
  border-radius: 18px;
  background: rgba(22, 23, 24, 0.91);
  box-shadow: 0 16px 42px rgba(0, 0, 0, 0.15);
}

.metric-card > div:last-child {
  display: flex;
  min-width: 0;
  flex-direction: column;
}

.metric-icon,
.panel-icon,
.capacity-category-icon {
  display: grid;
  place-items: center;
  border-radius: 13px;
}

.metric-icon {
  width: 43px;
  height: 43px;
}

.metric-icon .v-icon {
  color: inherit;
}

.metric-yellow .metric-icon {
  color: var(--accent);
  background: rgba(244, 211, 94, 0.08);
}

.metric-blue .metric-icon {
  color: #83a6ff;
  background: rgba(91, 134, 239, 0.09);
}

.metric-green .metric-icon {
  color: #70d393;
  background: rgba(74, 194, 116, 0.09);
}

.metric-coral .metric-icon {
  color: #ff9a72;
  background: rgba(255, 121, 79, 0.08);
}

.metric-card > div > strong {
  margin: 2px 0;
  color: var(--ink);
  font-size: 1.6875rem;
  font-weight: 950;
  line-height: 1;
  letter-spacing: -0.0625rem;
  overflow-wrap: anywhere;
}

.metric-card > div > strong.currency-value {
  font-size: 1.5625rem;
}

.metric-card > div > span {
  color: #77797d;
  font-size: 0.875rem;
  line-height: 1.45;
}

.workspace-grid {
  display: grid;
  margin-top: 16px;
  grid-template-columns: minmax(0, 2fr) minmax(300px, 0.8fr);
  align-items: stretch;
  gap: 16px;
}

.admin-panel {
  border: 1px solid var(--stroke) !important;
  border-radius: 22px !important;
  background: var(--surface) !important;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.17) !important;
}

.settings-panel {
  padding: 25px;
}

.panel-heading,
.registrations-heading {
  display: flex;
  margin-bottom: 22px;
  align-items: center;
  justify-content: space-between;
  gap: 20px;
}

.panel-title {
  display: flex;
  align-items: center;
  gap: 11px;
}

.panel-icon {
  width: 40px;
  height: 40px;
  color: var(--accent);
  border: 1px solid rgba(244, 211, 94, 0.16);
  background: rgba(244, 211, 94, 0.055);
}

.panel-icon .v-icon {
  color: var(--accent);
}

.panel-title > div {
  display: flex;
  flex-direction: column;
}

.panel-title h2 {
  margin: 1px 0 0;
  font-size: 1.125rem;
  font-weight: 900;
  letter-spacing: -0.025rem;
}

.unsaved-hint {
  color: #707276;
  font-size: 0.875rem;
}

.launch-control {
  display: flex;
  min-height: 76px;
  padding: 15px 18px;
  align-items: center;
  justify-content: space-between;
  gap: 20px;
  border: 1px solid rgba(255, 255, 255, 0.08);
  border-radius: 16px;
  background: rgba(255, 255, 255, 0.03);
  transition: border-color 180ms ease, background 180ms ease;
}

.launch-control.enabled {
  border-color: rgba(244, 211, 94, 0.28);
  background: rgba(244, 211, 94, 0.045);
}

.homepage-control {
  margin-top: 12px;
}

.launch-copy {
  display: flex;
  min-width: 0;
  align-items: center;
  gap: 12px;
}

.launch-light {
  width: 10px;
  height: 10px;
  flex: 0 0 auto;
  border-radius: 999px;
  background: #77797d;
  box-shadow: 0 0 0 5px rgba(255, 255, 255, 0.035);
}

.launch-control.enabled .launch-light {
  background: var(--accent);
  box-shadow: 0 0 0 5px rgba(244, 211, 94, 0.1);
}

.launch-copy > div {
  display: flex;
  min-width: 0;
  flex-direction: column;
}

.launch-copy strong {
  font-size: 1rem;
  font-weight: 900;
}

.launch-copy small {
  margin-top: 3px;
  color: #77797d;
  font-size: 0.875rem;
  line-height: 1.4;
}

.settings-fields {
  display: grid;
  margin-top: 19px;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 12px;
}

.field-span-2 {
  grid-column: span 2;
}

.settings-footer {
  display: flex;
  margin-top: 21px;
  padding-top: 19px;
  align-items: center;
  justify-content: space-between;
  gap: 18px;
  border-top: 1px solid rgba(255, 255, 255, 0.07);
}

.settings-footer > div {
  display: flex;
  align-items: center;
  gap: 7px;
  color: #717377;
  font-size: 0.875rem;
}

.settings-footer .v-icon {
  color: #6bc98d;
}

.admin-primary {
  min-height: 47px !important;
  padding: 0 20px !important;
  color: var(--accent-ink) !important;
  border-radius: 13px !important;
  background: var(--accent) !important;
  font-size: 0.875rem !important;
  font-weight: 950 !important;
  letter-spacing: 0 !important;
  text-transform: none !important;
  box-shadow: 0 13px 27px rgba(244, 211, 94, 0.13) !important;
}

.admin-primary .v-icon {
  color: var(--accent-ink) !important;
}

.side-stack {
  display: grid;
  grid-template-rows: auto 1fr;
  gap: 16px;
}

.status-panel,
.shirts-panel {
  padding: 22px;
}

.compact-heading {
  margin-bottom: 18px;
}

.registration-signal {
  display: flex;
  min-height: 96px;
  padding: 17px;
  align-items: center;
  gap: 16px;
  border: 1px solid rgba(255, 255, 255, 0.075);
  border-radius: 17px;
  background: rgba(255, 255, 255, 0.027);
}

.signal-orbit {
  display: grid;
  width: 52px;
  height: 52px;
  flex: 0 0 auto;
  place-items: center;
  border: 1px solid rgba(255, 255, 255, 0.09);
  border-radius: 999px;
}

.signal-orbit i {
  width: 13px;
  height: 13px;
  border-radius: 999px;
  background: #707276;
}

.registration-signal.online .signal-orbit {
  border-color: rgba(69, 194, 113, 0.21);
  box-shadow: inset 0 0 20px rgba(69, 194, 113, 0.05);
}

.registration-signal.online .signal-orbit i {
  background: #66d18c;
  box-shadow: 0 0 0 6px rgba(69, 194, 113, 0.1);
}

.registration-signal > div {
  display: flex;
  min-width: 0;
  flex-direction: column;
}

.registration-signal strong {
  color: #a6a8aa;
  font-size: 1.125rem;
  font-weight: 950;
  letter-spacing: 0.05rem;
}

.registration-signal.online strong {
  color: #75d597;
}

.registration-signal small {
  margin-top: 3px;
  color: #737579;
  font-size: 0.875rem;
  line-height: 1.45;
  overflow-wrap: anywhere;
}

.status-checks {
  display: grid;
  margin-top: 16px;
  gap: 9px;
}

.status-checks > div {
  display: flex;
  align-items: center;
  gap: 8px;
  color: #77797d;
  font-size: 0.875rem;
}

.status-checks .v-icon {
  color: #67696d;
}

.status-checks > div.done {
  color: #a5a7a8;
}

.status-checks > div.done .v-icon {
  color: #68ca8c;
}

.shirt-total,
.result-count {
  padding: 5px 8px;
  color: var(--accent);
  border-radius: 999px;
  background: rgba(244, 211, 94, 0.07);
  font-size: 0.75rem;
  font-weight: 900;
  letter-spacing: 0.04375rem;
}

.shirt-list {
  display: grid;
  gap: 11px;
}

.shirt-row {
  display: grid;
  grid-template-columns: 34px minmax(0, 1fr) 25px;
  align-items: center;
  gap: 9px;
}

.shirt-size {
  color: #b7b8b8;
  font-size: 0.875rem;
  font-weight: 900;
}

.shirt-row strong {
  text-align: right;
  font-size: 1rem;
}

.shirt-bar,
.capacity-track {
  overflow: hidden;
  position: relative;
  height: 6px;
  border-radius: 999px;
  background: rgba(255, 255, 255, 0.065);
}

.shirt-bar i,
.capacity-track i {
  display: block;
  height: 100%;
  border-radius: inherit;
  background: var(--accent);
  transition: width 350ms ease;
}

.empty-compact {
  display: flex;
  min-height: 135px;
  align-items: center;
  justify-content: center;
  flex-direction: column;
  gap: 8px;
  color: #66686b;
  font-size: 0.875rem;
}

.empty-compact .v-icon {
  color: #56585b;
}

.capacity-panel,
.registrations-panel {
  margin-top: 16px;
  padding: 25px;
}

.capacity-grid {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 12px;
}

.capacity-card {
  padding: 17px;
  border: 1px solid rgba(255, 255, 255, 0.075);
  border-radius: 17px;
  background: rgba(255, 255, 255, 0.026);
  transition: border-color 180ms ease, transform 180ms ease;
}

.capacity-card:hover {
  border-color: rgba(244, 211, 94, 0.2);
  transform: translateY(-2px);
}

.capacity-card.full {
  border-color: rgba(255, 112, 86, 0.18);
}

.capacity-head {
  display: grid;
  grid-template-columns: 38px minmax(0, 1fr) auto;
  align-items: center;
  gap: 10px;
}

.capacity-category-icon {
  width: 38px;
  height: 38px;
  color: var(--accent);
  background: rgba(244, 211, 94, 0.065);
}

.capacity-category-icon .v-icon {
  color: var(--accent);
}

.capacity-head > div {
  display: flex;
  min-width: 0;
  flex-direction: column;
}

.capacity-head strong {
  font-size: 1rem;
  font-weight: 900;
  line-height: 1.35;
  overflow-wrap: anywhere;
}

.capacity-head small {
  margin-top: 2px;
  color: #717377;
  font-size: 0.875rem;
}

.capacity-state {
  padding: 4px 6px;
  color: #70ce91;
  border-radius: 999px;
  background: rgba(69, 194, 113, 0.08);
  font-size: 0.75rem;
  font-weight: 950;
  letter-spacing: 0.0375rem;
}

.capacity-state.full {
  color: #ff9274;
  background: rgba(255, 112, 86, 0.08);
}

.capacity-numbers {
  display: flex;
  margin: 19px 0 9px;
  align-items: flex-end;
  justify-content: space-between;
}

.capacity-numbers strong {
  font-size: 1.6875rem;
  font-weight: 950;
  line-height: 1;
  letter-spacing: -0.0625rem;
}

.capacity-numbers span {
  color: #77797d;
  font-size: 0.875rem;
  font-weight: 800;
}

.capacity-numbers small {
  color: #727478;
  font-size: 0.875rem;
}

.capacity-track {
  height: 7px;
}

.capacity-foot {
  display: flex;
  min-height: 38px;
  margin-top: 11px;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
}

.capacity-foot > span {
  color: #77797d;
  font-size: 0.875rem;
  font-weight: 750;
}

.capacity-action {
  min-width: 0 !important;
  padding: 0 3px !important;
  color: var(--accent) !important;
  font-size: 0.875rem !important;
  font-weight: 850 !important;
  letter-spacing: 0 !important;
  text-transform: none !important;
}

.capacity-action .v-icon {
  color: var(--accent) !important;
}

.registrations-heading {
  margin-bottom: 18px;
}

.filter-bar {
  display: grid;
  margin-bottom: 17px;
  padding: 13px;
  grid-template-columns:
    minmax(240px, 1fr)
    minmax(175px, 0.4fr)
    minmax(175px, 0.4fr)
    auto;
  gap: 10px;
  border: 1px solid rgba(255, 255, 255, 0.07);
  border-radius: 15px;
  background: rgba(255, 255, 255, 0.025);
}

.filter-export {
  padding: 0 14px;
  gap: 7px;
  border-radius: 11px;
  font-size: 0.875rem;
  font-weight: 850;
}

.mobile-registration-list {
  display: none;
}

.desktop-registration-table {
  overflow-x: auto;
  scrollbar-gutter: stable;
}

.date-cell {
  display: flex;
  flex-direction: column;
}

.date-cell strong {
  color: #c4c5c5;
  font-size: 0.875rem;
}

.date-cell span {
  color: #696b6f;
  font-size: 0.875rem;
}

.category-cell {
  color: #d7d7d5;
  font-size: 1rem;
  font-weight: 850;
}

.table-participants {
  display: grid;
  padding: 6px 0;
  gap: 5px;
}

.table-person {
  display: grid;
  grid-template-columns: 30px minmax(0, 1fr) 8px auto;
  align-items: center;
  gap: 8px;
}

.person-avatar {
  display: grid;
  width: 30px;
  height: 30px;
  flex: 0 0 auto;
  place-items: center;
  color: var(--accent-ink);
  border-radius: 9px;
  background: var(--accent);
  font-size: 0.75rem;
  font-weight: 950;
}

.table-person > div {
  display: flex;
  min-width: 0;
  flex-direction: column;
}

.table-person strong {
  color: #d3d3d1;
  font-size: 1rem;
  line-height: 1.35;
  overflow-wrap: anywhere;
}

.table-person small {
  color: #6e7074;
  font-size: 0.875rem;
  line-height: 1.4;
  overflow-wrap: anywhere;
}

.member-dot {
  width: 7px;
  height: 7px;
  border-radius: 999px;
  background: #ff9d64;
  box-shadow: 0 0 0 3px rgba(255, 157, 100, 0.08);
}

.member-dot.active {
  background: #68cf8d;
  box-shadow: 0 0 0 3px rgba(74, 194, 116, 0.08);
}

.shirt-chip {
  padding: 4px 6px;
  color: #d3bd6a;
  border: 1px solid rgba(244, 211, 94, 0.14);
  border-radius: 7px;
  background: rgba(244, 211, 94, 0.045);
  font-size: 0.75rem;
  font-weight: 950;
}

.time-cell {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  color: #b3b4b5;
  font-size: 0.875rem;
  font-weight: 850;
}

.time-cell .v-icon {
  color: #77797d;
}

.fee-cell {
  color: #d9d9d6;
  font-size: 1rem;
}

.fee-stack {
  display: inline-flex;
  align-items: flex-start;
  flex-direction: column;
  gap: 4px;
}

.manual-badge {
  padding: 3px 5px;
  color: var(--accent);
  border-radius: 5px;
  background: rgba(244, 211, 94, 0.08);
  font-size: 0.75rem;
  font-weight: 950;
  letter-spacing: 0.0375rem;
}

.status-badge {
  display: inline-flex;
  min-height: 23px;
  padding: 0 7px;
  align-items: center;
  border-radius: 999px;
  font-size: 0.75rem;
  font-weight: 950;
  letter-spacing: 0.021875rem;
  white-space: nowrap;
}

.payment-paid,
.registration-active {
  color: #74d396;
  background: rgba(74, 194, 116, 0.09);
}

.payment-unpaid,
.registration-pending_payment {
  color: #ffb371;
  background: rgba(255, 151, 70, 0.09);
}

.payment-failed,
.registration-payment_review {
  color: #ff8c78;
  background: rgba(255, 99, 76, 0.09);
}

.payment-refunded {
  color: #8eb0ff;
  background: rgba(91, 134, 239, 0.09);
}

.registration-cancelled,
.registration-expired {
  color: #9b9da0;
  background: rgba(255, 255, 255, 0.06);
}

.more-action {
  color: #85878a !important;
}

.table-empty,
.mobile-empty {
  display: flex;
  min-height: 160px;
  align-items: center;
  justify-content: center;
  flex-direction: column;
  gap: 8px;
  color: #696b6f;
  font-size: 0.875rem;
}

.table-empty .v-icon,
.mobile-empty .v-icon {
  color: #55575a;
}

::v-deep .payment-confirmation-dialog {
  border-radius: 24px;
}

.payment-confirmation-card {
  overflow: hidden !important;
  padding: 34px;
  color: #f8f7f2 !important;
  border: 1px solid rgba(255, 255, 255, 0.11) !important;
  border-radius: 24px !important;
  background: radial-gradient(circle at 100% 0, rgba(244, 211, 94, 0.1), transparent 19rem), #151617 !important;
  box-shadow: 0 34px 100px rgba(0, 0, 0, 0.52) !important;
  font-size: 1rem;
  text-align: center;
}

.payment-confirmation-icon {
  display: grid;
  width: 64px;
  height: 64px;
  margin: 0 auto 18px;
  place-items: center;
  color: #18170f;
  border-radius: 20px;
  background: #f4d35e;
  box-shadow: 0 17px 35px rgba(244, 211, 94, 0.14);
}

.payment-confirmation-icon.refund {
  color: #eaf0ff;
  background: #547ee5;
  box-shadow: 0 17px 35px rgba(84, 126, 229, 0.18);
}

.payment-confirmation-icon .v-icon {
  color: inherit;
}

.payment-confirmation-eyebrow {
  margin: 0 0 7px !important;
  color: #f4d35e;
  font-size: 0.75rem !important;
  font-weight: 950;
  letter-spacing: 0.09375rem;
  text-transform: uppercase;
}

.payment-confirmation-card h2 {
  margin: 0;
  color: #f8f7f2;
  font-size: 1.625rem;
  font-weight: 950;
  line-height: 1.12;
  letter-spacing: -0.04375rem;
}

.payment-confirmation-card > p:not(.payment-confirmation-eyebrow) {
  margin: 15px 0 0;
  color: #a9aaad;
  font-size: 1rem;
  line-height: 1.6;
}

.payment-confirmation-actions {
  display: flex;
  margin-top: 27px;
  align-items: center;
  justify-content: flex-end;
  gap: 10px;
}

.payment-confirmation-actions .admin-primary {
  min-width: 190px;
}

.manual-dialog-card {
  --accent: #f4d35e;
  --accent-ink: #18170f;
  overflow: hidden !important;
  color: #f8f7f2 !important;
  border: 1px solid rgba(255, 255, 255, 0.11) !important;
  border-radius: 24px !important;
  background: radial-gradient(circle at 100% 0, rgba(244, 211, 94, 0.08), transparent 20rem), #151617 !important;
  box-shadow: 0 34px 100px rgba(0, 0, 0, 0.5) !important;
}

.manual-dialog-header {
  display: flex;
  min-height: 82px;
  padding: 18px 23px;
  align-items: center;
  justify-content: space-between;
  gap: 20px;
  border-bottom: 1px solid rgba(255, 255, 255, 0.075);
}

.manual-dialog-title {
  display: flex;
  min-width: 0;
  align-items: center;
  gap: 12px;
}

.manual-dialog-title > span {
  display: grid;
  width: 43px;
  height: 43px;
  flex: 0 0 auto;
  place-items: center;
  color: var(--accent);
  border: 1px solid rgba(244, 211, 94, 0.2);
  border-radius: 13px;
  background: rgba(244, 211, 94, 0.065);
}

.manual-dialog-title > span .v-icon {
  color: var(--accent);
}

.manual-dialog-title > div {
  display: flex;
  min-width: 0;
  flex-direction: column;
}

.manual-dialog-title small {
  color: var(--accent);
  font-size: 0.75rem;
  font-weight: 950;
  letter-spacing: 0.08125rem;
}

.manual-dialog-title h2 {
  margin: 2px 0 0;
  font-size: 1.25rem;
  font-weight: 950;
  line-height: 1.1;
  letter-spacing: -0.03125rem;
}

.manual-close {
  color: #85878b !important;
}

.manual-dialog-body {
  padding: 21px 25px 8px !important;
}

.manual-info {
  display: flex;
  min-height: 54px;
  padding: 12px 14px;
  align-items: center;
  gap: 10px;
  color: #aaa9a3;
  border: 1px solid rgba(244, 211, 94, 0.14);
  border-radius: 14px;
  background: rgba(244, 211, 94, 0.04);
  font-size: 1rem;
  line-height: 1.5;
}

.manual-info .v-icon {
  flex: 0 0 auto;
  color: var(--accent);
}

.manual-form-section {
  padding: 23px 0;
}

.manual-form-section + .manual-form-section {
  border-top: 1px solid rgba(255, 255, 255, 0.07);
}

.manual-section-heading {
  display: flex;
  margin-bottom: 16px;
  align-items: flex-start;
  gap: 11px;
}

.manual-section-heading > span {
  display: grid;
  width: 28px;
  height: 28px;
  flex: 0 0 auto;
  place-items: center;
  color: var(--accent-ink);
  border-radius: 9px;
  background: var(--accent);
  font-size: 0.75rem;
  font-weight: 950;
}

.manual-section-heading > div {
  display: flex;
  flex-direction: column;
}

.manual-section-heading strong {
  font-size: 1rem;
  font-weight: 900;
}

.manual-section-heading small {
  margin-top: 2px;
  color: #74767a;
  font-size: 0.875rem;
}

.manual-user-search {
  margin-bottom: 5px;
}

.manual-user-loading {
  display: flex;
  min-height: 56px;
  padding: 0 13px;
  align-items: center;
  gap: 10px;
  color: #7f8185;
  font-size: 0.875rem;
}

.manual-selected-user {
  display: grid;
  min-height: 65px;
  margin-top: 10px;
  padding: 11px 13px;
  grid-template-columns: 38px minmax(0, 1fr) auto;
  align-items: center;
  gap: 10px;
  border: 1px solid rgba(103, 207, 143, 0.15);
  border-radius: 14px;
  background: rgba(75, 191, 119, 0.045);
}

.manual-user-avatar {
  display: grid;
  width: 38px;
  height: 38px;
  place-items: center;
  color: var(--accent-ink);
  border-radius: 11px;
  background: var(--accent);
  font-size: 0.75rem;
  font-weight: 950;
}

.manual-selected-user > div {
  display: flex;
  min-width: 0;
  flex-direction: column;
}

.manual-selected-user strong,
.manual-selected-user small {
  overflow-wrap: anywhere;
}

.manual-selected-user strong {
  font-size: 1rem;
  font-weight: 900;
}

.manual-selected-user small {
  margin-top: 2px;
  color: #7f8185;
  font-size: 0.875rem;
}

.manual-member-pill {
  padding: 5px 7px;
  color: #72d397;
  border-radius: 999px;
  background: rgba(74, 194, 116, 0.09);
  font-size: 0.75rem;
  font-weight: 950;
  letter-spacing: 0.028125rem;
}

.manual-field-grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 12px;
}

.manual-field-span-2 {
  grid-column: span 2;
}

.manual-alert {
  margin: 2px 0 16px !important;
  border-radius: 12px !important;
  font-size: 0.875rem !important;
}

.manual-dialog-footer {
  display: flex;
  min-height: 76px;
  padding: 14px 23px;
  align-items: center;
  justify-content: flex-end;
  gap: 10px;
  border-top: 1px solid rgba(255, 255, 255, 0.075);
  background: rgba(8, 9, 10, 0.22);
}

.manual-cancel {
  min-height: 43px;
  padding: 0 13px;
  color: #8a8c90;
  border: 0;
  background: transparent;
  font-size: 0.875rem;
  font-weight: 800;
  cursor: pointer;
}

.manual-cancel:disabled {
  opacity: 0.45;
  cursor: default;
}

.manual-submit {
  min-width: 180px;
}

::v-deep .v-text-field--outlined fieldset {
  border-color: rgba(255, 255, 255, 0.1);
  border-radius: 11px;
}

::v-deep .v-text-field--outlined.v-input--is-focused fieldset {
  border-color: var(--accent) !important;
}

::v-deep .v-input {
  color: var(--ink);
  font-size: 1rem;
}

::v-deep .v-label {
  color: #7f8185 !important;
  font-size: 0.875rem;
}

::v-deep .v-input input {
  color: #d6d6d4 !important;
}

::v-deep .registration-table {
  min-width: 82.5rem;
  background: transparent !important;
}

::v-deep .registration-table thead th {
  height: 44px;
  color: #6f7175 !important;
  border-bottom-color: rgba(255, 255, 255, 0.07) !important;
  font-size: 0.75rem !important;
  font-weight: 900 !important;
  letter-spacing: 0.040625rem;
  text-transform: uppercase;
}

::v-deep .registration-table tbody td {
  border-bottom-color: rgba(255, 255, 255, 0.055) !important;
}

::v-deep .registration-table tbody tr {
  transition: background 160ms ease;
}

::v-deep .registration-table tbody tr:hover {
  background: rgba(244, 211, 94, 0.025) !important;
}

::v-deep .registration-table .v-data-footer {
  color: #797b7f;
  border-top-color: rgba(255, 255, 255, 0.07);
  font-size: 0.875rem;
}

@media (max-width: 1180px) {
  .metric-grid {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }

  .workspace-grid {
    grid-template-columns: minmax(0, 1.55fr) minmax(270px, 0.75fr);
  }

  .capacity-grid {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }

  .desktop-registration-table {
    overflow-x: auto;
  }

  ::v-deep .registration-table {
    min-width: 82.5rem;
  }
}

@media (max-width: 900px) {
  .admin-container {
    width: min(100% - 30px, 760px);
    padding-top: 20px;
  }

  .admin-topbar {
    padding: 0 15px;
  }

  .manual-action,
  .preview-action,
  .export-action {
    width: 40px;
    padding: 0;
  }

  .manual-action span,
  .preview-action span,
  .export-action span {
    display: none;
  }

  .admin-hero {
    padding: 27px;
  }

  .admin-hero h1 {
    font-size: 2.25rem;
  }

  .admin-hero p {
    max-width: 450px;
  }

  .workspace-grid {
    grid-template-columns: 1fr;
  }

  .side-stack {
    grid-template-columns: 1fr 1fr;
    grid-template-rows: auto;
  }

  .desktop-registration-table {
    display: none;
  }

  .mobile-registration-list {
    display: grid;
    gap: 10px;
  }

  .filter-bar {
    grid-template-columns: minmax(0, 1fr) minmax(180px, 0.65fr);
  }

  .filter-export {
    display: none;
  }

  .mobile-registration-card {
    padding: 17px;
    border: 1px solid rgba(255, 255, 255, 0.075);
    border-radius: 17px;
    background: rgba(255, 255, 255, 0.027);
  }

  .mobile-registration-head {
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
  }

  .mobile-registration-head > div:first-child {
    display: flex;
    flex-direction: column;
  }

  .mobile-registration-head small {
    color: #696b6f;
    font-size: 0.75rem;
  }

  .mobile-registration-head strong {
    margin-top: 3px;
    font-size: 1rem;
    font-weight: 900;
  }

  .mobile-participants {
    display: grid;
    margin-top: 14px;
    gap: 7px;
  }

  .mobile-person {
    display: grid;
    padding: 10px;
    grid-template-columns: 34px minmax(0, 1fr) auto;
    align-items: center;
    gap: 9px;
    border-radius: 12px;
    background: rgba(255, 255, 255, 0.03);
  }

  .mobile-person .person-avatar {
    width: 34px;
    height: 34px;
    border-radius: 10px;
  }

  .mobile-person > div {
    display: flex;
    min-width: 0;
    flex-direction: column;
  }

  .mobile-person strong {
    font-size: 1rem;
    line-height: 1.35;
    overflow-wrap: anywhere;
  }

  .mobile-person small {
    color: #6f7175;
    font-size: 0.875rem;
    line-height: 1.4;
    overflow-wrap: anywhere;
  }

  .mobile-registration-meta {
    display: grid;
    margin-top: 12px;
    padding-top: 12px;
    grid-template-columns: repeat(4, minmax(0, 1fr));
    gap: 8px;
    border-top: 1px solid rgba(255, 255, 255, 0.065);
  }

  .mobile-registration-meta > div {
    display: flex;
    flex-direction: column;
    gap: 4px;
  }

  .mobile-registration-meta strong {
    font-size: 0.875rem;
  }
}

@media (max-width: 620px) {
  .admin-topbar {
    min-height: 62px;
    grid-template-columns: 1fr auto;
  }

  .admin-brand > div,
  .live-status {
    display: none;
  }

  .admin-brand-mark {
    width: 36px;
    height: 36px;
  }

  .export-action span {
    display: none;
  }

  .export-action,
  .preview-action {
    width: 40px;
    padding: 0;
  }

  .preview-action span {
    display: none;
  }

  .admin-container {
    width: calc(100% - 20px);
    padding-top: 10px;
  }

  .admin-hero {
    min-height: 152px;
    padding: 23px 20px;
    border-radius: 21px;
  }

  .admin-hero h1 {
    font-size: 1.75rem;
    letter-spacing: -0.0625rem;
  }

  .admin-hero p {
    display: none;
  }

  .hero-health {
    min-width: auto;
  }

  .health-ring {
    width: 73px;
    height: 73px;
  }

  .health-copy {
    display: none;
  }

  .metric-grid {
    grid-template-columns: 1fr 1fr;
    gap: 8px;
  }

  .metric-card {
    min-height: 100px;
    padding: 13px;
    grid-template-columns: 36px minmax(0, 1fr);
    gap: 9px;
    border-radius: 15px;
  }

  .metric-icon {
    width: 36px;
    height: 36px;
    border-radius: 11px;
  }

  .metric-card > div > strong {
    font-size: 1.375rem;
  }

  .metric-card > div > strong.currency-value {
    font-size: 1rem;
    line-height: 1.2;
  }

  .metric-card > div > span {
    display: none;
  }

  .settings-panel,
  .status-panel,
  .shirts-panel,
  .capacity-panel,
  .registrations-panel {
    padding: 18px 14px;
    border-radius: 19px !important;
  }

  .panel-heading,
  .registrations-heading {
    margin-bottom: 17px;
  }

  .panel-title h2 {
    font-size: 1rem;
  }

  .panel-icon {
    width: 36px;
    height: 36px;
    border-radius: 11px;
  }

  .unsaved-hint {
    display: none;
  }

  .launch-control {
    padding: 13px;
  }

  .launch-copy small {
    max-width: 190px;
  }

  .settings-fields {
    grid-template-columns: 1fr;
    gap: 10px;
  }

  .field-span-2 {
    grid-column: span 1;
  }

  .settings-footer {
    align-items: stretch;
    flex-direction: column;
  }

  .settings-footer > div {
    display: none;
  }

  .admin-primary {
    width: 100%;
  }

  .side-stack {
    grid-template-columns: 1fr;
  }

  .capacity-grid {
    grid-template-columns: 1fr;
  }

  .capacity-card:hover {
    transform: none;
  }

  .filter-bar {
    padding: 10px;
    grid-template-columns: 1fr;
  }

  .mobile-registration-card {
    padding: 14px 12px;
  }

  .mobile-person {
    grid-template-columns: 33px minmax(0, 1fr);
  }

  .mobile-person .shirt-chip {
    grid-column: 2;
    width: max-content;
  }

  .mobile-registration-meta {
    grid-template-columns: 1fr 1fr;
    row-gap: 13px;
  }

  ::v-deep .payment-confirmation-dialog {
    width: calc(100% - 24px);
    margin: 12px;
  }

  .payment-confirmation-card {
    padding: 29px 18px 20px;
    border-radius: 20px !important;
  }

  .payment-confirmation-card h2 {
    font-size: 1.375rem;
  }

  .payment-confirmation-actions {
    align-items: stretch;
    flex-direction: column-reverse;
  }

  .payment-confirmation-actions .manual-cancel,
  .payment-confirmation-actions .admin-primary {
    width: 100%;
  }

  .manual-dialog-card {
    border-radius: 19px !important;
  }

  .manual-dialog-header {
    min-height: 72px;
    padding: 14px 15px;
  }

  .manual-dialog-title > span {
    width: 38px;
    height: 38px;
    border-radius: 11px;
  }

  .manual-dialog-title h2 {
    max-width: 205px;
    font-size: 1rem;
  }

  .manual-dialog-body {
    padding: 15px 14px 4px !important;
  }

  .manual-info {
    align-items: flex-start;
  }

  .manual-field-grid {
    grid-template-columns: 1fr;
    gap: 10px;
  }

  .manual-field-span-2 {
    grid-column: span 1;
  }

  .manual-selected-user {
    grid-template-columns: 36px minmax(0, 1fr);
  }

  .manual-member-pill {
    grid-column: 2;
    width: max-content;
  }

  .manual-dialog-footer {
    min-height: 72px;
    padding: 12px 14px;
  }

  .manual-submit {
    min-width: 0;
    flex: 1;
  }
}

@media (prefers-reduced-motion: reduce) {
  *,
  *::before,
  *::after {
    transition-duration: 0.01ms !important;
    animation-duration: 0.01ms !important;
  }
}
</style>
